/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { SearchStrategy } from 'src/core/utils/searchStrategies';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { BracesHandlerProps } from 'src/luis/verticals/patternInput/components/bracesHandlerProps';
import { PatternDropdownType } from 'src/luis/verticals/patternInput/types';

const messages = defineMessages({
    entitiesTitle: {
        id: 'patterns.patternInput.entitiesTitle',
        defaultMessage: 'Entities'
    },
    noEntitiesFoundTitle: {
        id: 'patterns.patternInput.noEntitiesFoundTitle',
        defaultMessage: 'No entities found'
    },
    rolesTitle: {
        id: 'patterns.patternInput.rolesTitle',
        defaultMessage: 'Roles'
    },
    noRolesFoundTitle: {
        id: 'patterns.patternInput.noRolesFoundTitle',
        defaultMessage: 'No roles found'
    },
    actionsTitle: {
        id: 'patterns.patternInput.actionsTitle',
        defaultMessage: 'Actions'
    },
    newEntityBtnLabel: {
        id: 'patterns.patternInput.newEntityBtnLabel',
        defaultMessage: 'Create new Entity'
    },
    newEntityAndRoleBtnLabel: {
        id: 'patterns.patternInput.newEntityAndRoleBtnLabel',
        defaultMessage: 'Create new entity and role'
    },
    newRoleBtnLabel: {
        id: 'patterns.patternInput.newRoleBtnLabel',
        defaultMessage: 'Create new Role'
    }
});

/**
 * Generates the items to display in the dropdown contextual
 * menu in the pattern input text field based on the current
 * state of the input field. The items are displayed following
 * these rules:
 *
 * - If the dropdown is in entity mode, display an entities
 *   header followed by the entities filtered by the user
 *   input field query (the text between the braces).
 *
 * - If the query filter results in no matches, show the
 *   no items found item and the create new entity action.
 *
 * - If the dropdown is in roles mode (the user query
 *   contained a colon), display a roles header followed by
 *   the roles filtered by the field query.
 *
 * - If the query filter results in no matches, show the
 *   no items option, followed by an action to either create
 *   a new entity and a new role, or a new role only. This is
 *   based on the following:
 *     - Check the name of the entity (the string before the
 *       colon) and the name of the role (the string after
 *       the colon). Ex:
 *
 *       "Location:Source" --> Entity: Location, Role: Source.
 *
 *     - If the entity had existed before, show the option
 *       to create a role only.
 *
 *     - If the entity is also new, show the option to create
 *       both a new entity and a new role.
 *
 * @param dropdownType The current type of the dropdown.
 * @param entities The entities of the application.
 * @param queryText The query text that the user is using.
 * @param callbacks The callbacks to fire when clicking actions.
 * @param localize The localization string function.
 * @returns The items to display in the menu.
 */
export const getPatternEntityDropdownItems = (
    dropdownType: PatternDropdownType,
    entities: Entity[],
    queryText: string,
    callbacks: Pick<BracesHandlerProps, 'onItemClicked' | 'onCreateNewEntity' | 'onCreateNewRole' | 'onCreateNewEntityAndRole'>,
    localize: ReturnType<typeof useLocalization>['localize']
) => {
    const [queryEntity, queryRole] = queryText.split(':', 2);
    const entityNames = entities.map(e => e.name);
    const roleNames = entities.reduce<string[]>((acc, e) => [...acc, ...e.roles.map(r => `${e.name}:${r.name}`)], []);
    const doesQueryEntityExist = entityNames.some(e => e === queryEntity);

    const filteredEntityNames = SearchStrategy.substringSearchStrategy(entityNames, queryText);
    const filteredRoleNames = SearchStrategy.substringSearchStrategy(roleNames, queryText);

    const entityItems: IContextualMenuItem[] = filteredEntityNames.map(name => ({
        key: name,
        text: name,
        onClick: (ev, item) => {
            ev.stopPropagation();
            callbacks.onItemClicked(item.text);
        }
    }));

    const roleItems: IContextualMenuItem[] = filteredRoleNames.map(name => ({
        key: name,
        text: name,
        onClick: (event, item) => {
            event.stopPropagation();
            callbacks.onItemClicked(item.text);
        }
    }));

    const entitiesHeaderItem: IContextualMenuItem = {
        key: 'entitiesHeaderItem',
        text: localize(messages.entitiesTitle),
        itemType: ContextualMenuItemType.Header
    };
    const rolesHeaderItem: IContextualMenuItem = {
        key: 'rolesHeaderItem',
        text: localize(messages.rolesTitle),
        itemType: ContextualMenuItemType.Header
    };
    const actionsHeaderItem: IContextualMenuItem = {
        key: 'actionsHeader',
        text: localize(messages.actionsTitle),
        itemType: ContextualMenuItemType.Header
    };

    const noEntitiesItem: IContextualMenuItem = {
        key: 'noEntitiesItem',
        text: localize(messages.noEntitiesFoundTitle),
        disabled: true
    };

    const noRolesItem: IContextualMenuItem = {
        key: 'noRolesItem',
        text: localize(messages.noRolesFoundTitle),
        disabled: true
    };

    const createEntityItem: IContextualMenuItem = {
        key: 'createEntityItem',
        iconProps: { iconName: 'Add' },
        text: localize(messages.newEntityBtnLabel),
        onClick: () => callbacks.onCreateNewEntity(queryEntity)
    };
    const createRoleItem: IContextualMenuItem = {
        key: 'createRoleItem',
        iconProps: { iconName: 'Add' },
        text: localize(messages.newRoleBtnLabel),
        onClick: () => callbacks.onCreateNewRole(queryEntity, queryRole)
    };
    const createEntityAndRoleItem: IContextualMenuItem = {
        key: 'createEntityAndRoleItem',
        iconProps: { iconName: 'Add' },
        text: localize(messages.newEntityAndRoleBtnLabel),
        onClick: () => callbacks.onCreateNewEntityAndRole(queryEntity, queryRole)
    };

    let items: IContextualMenuItem[] = [];

    if (dropdownType === 'entities') {
        items.push(entitiesHeaderItem);

        if (!entityItems.length) {
            items.push(noEntitiesItem);
            items.push(actionsHeaderItem);
            items.push(createEntityItem);
        } else {
            items = items.concat(entityItems);
        }
    }

    if (dropdownType === 'roles') {
        items.push(rolesHeaderItem);

        if (!roleItems.length) {
            items.push(noRolesItem);
            items.push(actionsHeaderItem);
            items.push(doesQueryEntityExist ? createRoleItem : createEntityAndRoleItem);
        } else {
            items = items.concat(roleItems);
        }
    }

    return items;
};
